import { useState, useRef, useEffect, useLayoutEffect, type ChangeEvent } from "react";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { format } from "date-fns";
import { ChevronLeft, Send, Loader2, Check, CheckCheck, Clock, AlertCircle, Paperclip, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTextConversation, useMarkTextsRead } from "@/hooks/useTextMessages";
import { useSendText } from "@/hooks/useSendText";
import { supabase } from "@/lib/supabase";
import { cn, formatError } from "@/lib/utils";
import { toast } from "sonner";
import type { TextMessage, TextReaction } from "@/lib/types";

const REACTION_EMOJI: Record<string, string> = {
  love: "\u2764\uFE0F",
  like: "\uD83D\uDC4D",
  dislike: "\uD83D\uDC4E",
  laugh: "\uD83D\uDE02",
  emphasize: "\u203C\uFE0F",
  question: "\u2753",
};

const REACTION_PATTERN = /^(Loved|Liked|Disliked|Laughed at|Emphasized|Questioned)\s+\u201c[\s\S]*\u201d\s*$/;

function isReactionMessage(msg: TextMessage): boolean {
  return REACTION_PATTERN.test(msg.body);
}

function ReactionBadges({ reactions, direction }: { reactions: TextReaction[]; direction: string }) {
  if (!reactions || reactions.length === 0) return null;
  const grouped = new Map<string, number>();
  for (const r of reactions) {
    grouped.set(r.type, (grouped.get(r.type) || 0) + 1);
  }
  const isOutbound = direction === "outbound";
  return (
    <div className={cn(
      "absolute -bottom-2.5 flex gap-px z-[1]",
      isOutbound ? "right-2" : "left-2"
    )}>
      {Array.from(grouped.entries()).map(([type, count]) => (
        <span
          key={type}
          className="inline-flex items-center justify-center h-[22px] min-w-[22px] bg-zinc-800 border border-zinc-700 rounded-full text-[13px] leading-none px-1 shadow-sm"
        >
          {REACTION_EMOJI[type] || type}
          {count > 1 && <span className="text-[10px] text-zinc-400 ml-0.5">{count}</span>}
        </span>
      ))}
    </div>
  );
}

function ReactionPicker({ onReact }: { onReact: (type: TextReaction["type"]) => void }) {
  const types: TextReaction["type"][] = ["love", "like", "dislike", "laugh", "emphasize", "question"];
  return (
    <div className="flex gap-1 bg-zinc-800 border border-zinc-700 rounded-full px-2 py-1 shadow-lg">
      {types.map((type) => (
        <button
          key={type}
          onClick={() => onReact(type)}
          className="hover:scale-125 transition-transform text-base leading-none"
        >
          {REACTION_EMOJI[type]}
        </button>
      ))}
    </div>
  );
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

type PendingAttachment = {
  file: File;
  preview: string;
};

function DeliveryIndicator({ msg }: { msg: TextMessage }) {
  if (msg.direction !== "outbound") return null;

  // Bridge status takes priority for messages sent from the web
  if (msg.bridge_status === "pending") {
    return <Clock className="h-3 w-3 inline-block ml-1 opacity-60" />;
  }
  if (msg.bridge_status === "failed") {
    return <AlertCircle className="h-3 w-3 inline-block ml-1 text-red-400" />;
  }

  // Delivery status from chat.db
  const status = msg.delivery_status;
  if (status === "read") {
    return <CheckCheck className="h-3 w-3 inline-block ml-1 text-blue-400" />;
  }
  if (status === "delivered") {
    return <CheckCheck className="h-3 w-3 inline-block ml-1 opacity-60" />;
  }
  if (status === "sent") {
    return <Check className="h-3 w-3 inline-block ml-1 opacity-60" />;
  }
  if (status === "failed") {
    return <AlertCircle className="h-3 w-3 inline-block ml-1 text-red-400" />;
  }

  return null;
}

type Props = {
  chatId: string;
  chatType: "individual" | "group";
  chatTitle: string | null;
  participants: { phone: string; name?: string | null }[] | null;
  phoneNumber: string;
  contactName: string | null;
  onBack: () => void;
};

function deriveHeaderTitle(
  chatType: "individual" | "group",
  chatTitle: string | null,
  participants: { phone: string; name?: string | null }[] | null,
  contactName: string | null,
  phoneNumber: string,
): string {
  if (chatType === "group") {
    if (chatTitle && chatTitle.trim()) return chatTitle.trim();
    const named = (participants || [])
      .map((p) => p.name || p.phone)
      .filter((s): s is string => !!s);
    if (named.length === 0) return "Group";
    if (named.length <= 2) return named.join(" & ");
    return `${named[0]}, ${named[1]} + ${named.length - 2}`;
  }
  return contactName || phoneNumber;
}

export function TextConversationView({
  chatId,
  chatType,
  chatTitle,
  participants,
  phoneNumber,
  contactName,
  onBack,
}: Props) {
  const { data: messages, isLoading } = useTextConversation(chatId);
  const sendText = useSendText();
  const markRead = useMarkTextsRead();

  useEffect(() => {
    markRead.mutate(chatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [reactionMsgId, setReactionMsgId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleReact = async (msgId: string, type: TextReaction["type"]) => {
    setReactionMsgId(null);
    const msg = messages?.find((m) => m.id === msgId);
    if (!msg) return;
    const existing = msg.reactions || [];
    const already = existing.find((r) => r.type === type && r.is_from_me);
    const updated = already
      ? existing.filter((r) => !(r.type === type && r.is_from_me))
      : [...existing, { type, is_from_me: true }];
    const { error } = await supabase
      .from("text_messages")
      .update({ reactions: updated })
      .eq("id", msgId);
    if (error) toast.error("Failed to react");
  };

  // Filter out legacy "Loved/Liked" text reaction messages
  const filteredMessages = messages?.filter((msg) => !isReactionMessage(msg));

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Hide chat FAB while this view is mounted
  useLayoutEffect(() => {
    document.body.setAttribute("data-hide-chat-fab", "true");
    return () => document.body.removeAttribute("data-hide-chat-fab");
  }, []);

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File too large: ${file.name} (max 10 MB)`);
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments((prev) => [
          ...prev,
          { file, preview: reader.result as string },
        ]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadToStorage = async (file: File): Promise<{ url: string; mime_type: string; name: string; size: number }> => {
    const ext = file.name.split(".").pop() || "bin";
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    const { error } = await supabase.storage
      .from("text-attachments")
      .upload(path, file, { contentType: file.type });

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from("text-attachments")
      .getPublicUrl(path);

    return {
      url: urlData.publicUrl,
      mime_type: file.type,
      name: file.name,
      size: file.size,
    };
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text && attachments.length === 0) return;

    const currentDraft = draft;
    const currentAttachments = [...attachments];
    setDraft("");
    setAttachments([]);

    try {
      // Upload any attachments to Supabase Storage
      let uploadedAttachments: { url: string; mime_type: string; name: string; size: number }[] = [];
      if (currentAttachments.length > 0) {
        setUploading(true);
        uploadedAttachments = await Promise.all(
          currentAttachments.map((att) => uploadToStorage(att.file))
        );
        setUploading(false);
      }

      await sendText.mutateAsync({
        phone_number: phoneNumber,
        body: text,
        contact_name: contactName,
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
        chat_id: chatId,
        chat_type: chatType,
        chat_title: chatTitle,
        participants,
      });
    } catch (err) {
      setUploading(false);
      toast.error(`Send failed: ${formatError(err)}`);
      setDraft(currentDraft); // restore on error
      setAttachments(currentAttachments);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isBusy = sendText.isPending || uploading;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-3 border-b border-border bg-background" style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top, 0px))" }}>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold truncate">
            {deriveHeaderTitle(chatType, chatTitle, participants, contactName, phoneNumber)}
          </p>
          {chatType === "group" ? (
            <p className="text-xs text-muted-foreground truncate">
              {(participants || [])
                .map((p) => p.name || p.phone)
                .filter(Boolean)
                .join(", ") || `${(participants?.length ?? 0) + 1} people`}
            </p>
          ) : contactName ? (
            <p className="text-xs text-muted-foreground">{phoneNumber}</p>
          ) : null}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 min-w-0 overflow-y-auto px-4 py-4 space-y-2" onClick={() => setReactionMsgId(null)}>
        {isLoading ? (
          <p className="text-center text-sm text-muted-foreground py-8">Loading...</p>
        ) : (
          filteredMessages?.map((msg) => {
            const hasReactions = msg.reactions?.length > 0;
            return (
            <div
              key={msg.id}
              className={cn(
                "max-w-[80%] sm:max-w-[420px] relative group",
                msg.direction === "outbound" ? "ml-auto" : "",
                hasReactions && "mb-2"
              )}
            >
              {/* Reaction picker */}
              {reactionMsgId === msg.id && (
                <div className={cn(
                  "absolute -top-10 z-10",
                  msg.direction === "outbound" ? "right-0" : "left-0"
                )} onClick={(e) => e.stopPropagation()}>
                  <ReactionPicker onReact={(type) => handleReact(msg.id, type)} />
                </div>
              )}
              <div
                className={cn(
                  "rounded-2xl text-sm",
                  msg.direction === "outbound"
                    ? "bg-primary/70 text-zinc-950 font-medium rounded-br-sm"
                    : "bg-muted rounded-bl-sm",
                  msg.attachments?.length > 0 ? "overflow-hidden" : "px-3 py-2"
                )}
                onDoubleClick={(e) => { e.stopPropagation(); setReactionMsgId(msg.id === reactionMsgId ? null : msg.id); }}
              >
                {msg.attachments?.length > 0 && (
                  <div className={cn(msg.body ? "pb-0" : "")}>
                    {msg.attachments.map((att, i) => (
                      <img
                        key={i}
                        src={att.url}
                        alt={att.name || "Image"}
                        className="w-full max-h-[400px] object-contain bg-zinc-900 cursor-pointer"
                        loading="lazy"
                        onClick={() => setLightboxSrc(att.url)}
                      />
                    ))}
                  </div>
                )}
                <div className={cn(msg.attachments?.length > 0 ? "px-3 py-2" : "")}>
                  {msg.body && <p className="whitespace-pre-wrap break-words">{msg.body}</p>}
                  <p className={cn(
                    "text-[10px] mt-1 flex items-center",
                    msg.direction === "outbound" ? "text-zinc-950/60 justify-end" : "text-muted-foreground"
                  )}>
                    {format(new Date(msg.created_at), "h:mm a")}
                  {msg.service_type && (
                    <span className="ml-1 opacity-60">· {msg.service_type}</span>
                  )}
                    <DeliveryIndicator msg={msg} />
                  </p>
                </div>
              </div>
              <ReactionBadges reactions={msg.reactions} direction={msg.direction} />
            </div>
            );
          }))
        }
        <div ref={bottomRef} />
      </div>

      {/* Reply input */}
      <div className="border-t border-border px-3 py-2">
        {/* Attachment previews */}
        {attachments.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {attachments.map((att, i) => (
              <div key={i} className="relative group">
                <img
                  src={att.preview}
                  alt={att.file.name}
                  className="h-16 rounded border border-border"
                />
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  className="absolute -top-1.5 -right-1.5 bg-background rounded-full"
                >
                  <XCircle className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBusy}
            title="Attach image"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="iMessage..."
            rows={1}
            className="flex-1 resize-none bg-muted rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary min-h-[36px] max-h-[120px]"
            style={{ fieldSizing: "content" } as React.CSSProperties}
          />
          <Button
            size="icon"
            className="h-9 w-9 rounded-full shrink-0"
            onClick={handleSend}
            disabled={isBusy || (!draft.trim() && attachments.length === 0)}
          >
            {isBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
    </div>
  );
}
